import * as React from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Separator } from "./ui/separator";

interface CollapsibleCardProps extends Omit<React.ComponentProps<"div">, "title" | "content"> {
  /**
   * Whether the card is collapsible
   */
  collapsible?: boolean;
  /**
   * Whether the card is initially expanded (only applies when collapsible is true)
   */
  defaultExpanded?: boolean;
  /**
   * Whether the card is currently expanded (controlled)
   */
  expanded?: boolean;
  /**
   * Callback when expansion state changes
   */
  onExpandedChange?: (expanded: boolean) => void;
  /**
   * The nesting level of this card (0 = root, 1 = first level child, etc.)
   */
  level?: number;
  /**
   * Whether this card has children
   */
  hasChildren?: boolean;
  /**
   * Custom content for the card header
   */
  children?: React.ReactNode;
  /**
   * Title of the card
   */
  title?: React.ReactNode;
  /**
   * Description of the card
   */
  description?: React.ReactNode;
  /**
   * Content of the card (shown when expanded)
   */
  content?: React.ReactNode;
  /**
   * Footer content of the card
   */
  footer?: React.ReactNode;
  /**
   * Whether to show the expand/collapse button
   */
  showToggle?: boolean;
  /**
   * Custom class name for the toggle button
   */
  toggleClassName?: string;
  /**
   * Whether to show visual hierarchy indicators
   */
  showHierarchy?: boolean;
  /**
   * Whether to show the separator below the header
   */
  showSeparator?: boolean;
  /**
   * Allow expanded content to grow beyond the default max height (for long lists)
   */
  unboundedContent?: boolean;
}

function CollapsibleCard({
  className,
  collapsible = false,
  defaultExpanded = true,
  expanded: controlledExpanded,
  onExpandedChange,
  level = 0,
  hasChildren = false,
  children,
  title,
  description,
  content,
  footer,
  showToggle = true,
  toggleClassName,
  showHierarchy = true,
  showSeparator = true,
  unboundedContent = false,
  ...props
}: CollapsibleCardProps) {
  const [internalExpanded, setInternalExpanded] = React.useState(defaultExpanded);
  const contentRef = React.useRef<HTMLDivElement>(null);
  
  const isExpanded = controlledExpanded !== undefined ? controlledExpanded : internalExpanded;

  const handleToggle = React.useCallback(() => {
    const newExpanded = !isExpanded;
    
    if (controlledExpanded === undefined) {
      setInternalExpanded(newExpanded);
    }
    onExpandedChange?.(newExpanded);
  }, [isExpanded, controlledExpanded, onExpandedChange]);

  const shouldShowToggle = collapsible && showToggle && (hasChildren || children);
  const hierarchyIndent = showHierarchy ? level * 16 : 0;
  const isNested = level > 0;

  return (
    <div
      className={cn(
        "relative min-w-0",
        isNested && showHierarchy && "ml-4 border-l-2 border-muted pl-4",
        className
      )}
      style={{ marginLeft: showHierarchy ? `${hierarchyIndent}px` : undefined }}
      {...props}
    >
      <Card
        className={cn(
          "min-w-0 overflow-hidden transition-all duration-300 ease-in-out",
          isNested && "bg-muted/30 border-muted/50",
          level === 0 && "bg-card",
          level === 1 && "bg-muted/20",
          level >= 2 && "bg-muted/10",
          (!isExpanded && collapsible) && "pb-0"
        )}
      >
        {(title || description || shouldShowToggle) && (
          <CardHeader className={cn(
            "pb-3 transition-all duration-300 ease-in-out",
            (!isExpanded && collapsible) && "pb-0"
          )}>
            <div className="flex items-center justify-between gap-2">
              <div className="flex-1 min-w-0">
                {title && (
                  <CardTitle className="text-base font-medium min-w-0 overflow-hidden">
                    {title}
                  </CardTitle>
                )}
                {description && (
                  <CardDescription className="text-sm text-muted-foreground mt-1">
                    {description}
                  </CardDescription>
                )}
              </div>
              {shouldShowToggle && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleToggle}
                  className={cn(
                    "h-8 w-8 p-0 shrink-0",
                    toggleClassName
                  )}
                  aria-label={isExpanded ? "Collapse" : "Expand"}
                >
                  <ChevronDown 
                    className={cn(
                      "h-4 w-4 transition-transform duration-200 ease-in-out",
                      isExpanded && "rotate-180"
                    )}
                  />
                </Button>
              )}
            </div>
            {showSeparator && (
              <div
                className={cn(
                  "transition-all duration-300 ease-in-out overflow-hidden",
                  isExpanded || !collapsible 
                    ? "max-h-4 opacity-100" 
                    : "max-h-0 opacity-0"
                )}
              >
                <Separator className="mt-1" />
              </div>
            )}
          </CardHeader>
        )}

        <div
          className={cn(
            "min-w-0 overflow-hidden transition-all duration-300 ease-in-out",
            isExpanded || !collapsible
              ? unboundedContent
                ? "max-h-none opacity-100"
                : "max-h-[1000px] opacity-100"
              : "max-h-0 opacity-0"
          )}
        >
          <CardContent className="pt-0 pb-4 min-w-0 transition-all duration-300 ease-in-out" ref={contentRef}>
            {content}
            {children}
          </CardContent>
          {footer && (
            <CardFooter className="pt-0 pb-4 transition-all duration-300 ease-in-out">
              {footer}
            </CardFooter>
          )}
        </div>
      </Card>
    </div>
  );
}

// Compound component for building hierarchical card structures
interface CollapsibleCardGroupProps {
  children: React.ReactNode;
  className?: string;
  level?: number;
}

function CollapsibleCardGroup({ 
  children, 
  className, 
  level = 0 
}: CollapsibleCardGroupProps) {
  return (
    <div 
      className={cn(
        "space-y-2",
        level > 0 && "ml-4 border-l-2 border-muted/30 pl-4",
        className
      )}
    >
      {React.Children.map(children, (child) => {
        if (React.isValidElement<CollapsibleCardProps>(child)) {
          return React.cloneElement(child, {
            level: child.props.level ?? level,
            showHierarchy: child.props.showHierarchy ?? true,
          });
        }
        return child;
      })}
    </div>
  );
}

export {
  CollapsibleCard,
  CollapsibleCardGroup,
  type CollapsibleCardProps,
  type CollapsibleCardGroupProps,
};
